import React, {useEffect, useMemo, useState} from "react";
import {Link, useParams, useSearchParams} from "react-router-dom";
import {LoadingFull} from "@/app/LoadingFull";
import {ErrorFull} from "@/component/layout/ErrorFull";
import {useAccountStore} from "@/store/useAccountStore";
import {DateNav} from "@/component/view/DateNav";
import {DateInput} from "@/component/input/DateInput";
import {useDialog} from "@/dialog/DialogContext";
import {FtDate} from "@/common/util/FtDate";
import {TransDto} from "@/common/dto/TransDto";
import {TransactionListView} from "@/component/view/TransactionListView";
import {FtButton} from "@/common/FtButton";
import {Device} from "@/store/Device";
import {useTransactionAccountSummary, useTransactions} from "@/hook/useTransactions";
import {ErrorUtil} from "@/utils/ErrorUtil";
import {MAX_TRANS_LIST_ITEM_COUNT} from "@/constatants";

interface SearchParams {
    ymdFrom: string;
    ymdTo: string;
}

export function AccountTransactionsPage() {
    const params = useParams();
    const accountId = parseInt(params.accountId as string);

    const [searchParams, setSearchParams] = useSearchParams();
    const ymdFrom = searchParams.get("ymdFrom") || new FtDate().setDate(1).ymd('-');
    const ymdTo = searchParams.get("ymdTo") || new FtDate().lastDayOfMonth().ymd('-');

    const account = useAccountStore(state => state.getAccountById(accountId));
    const getAccountNameById = useAccountStore(state => state.getAccountNameById);

    const {data: transactions, error} = useTransactions({
        accountId: accountId,
        transactDateFrom: ymdFrom,
        transactDateTo: ymdTo,
    });
    const {data: sumBefore} = useTransactionAccountSummary(accountId, undefined, new FtDate(ymdFrom).addDay(-1).ymd('-'));
    const [searchParamsInput, setSearchParamsInput] = useState<SearchParams>({ymdFrom, ymdTo});

    useEffect(() => {
        setSearchParamsInput({ymdFrom, ymdTo});
    }, [ymdFrom, ymdTo]);

    const dialog = useDialog();

    const items = useMemo(() => {
        if (!transactions || !account) return [];

        return transactions.map(t => ({
            key: t.id.toString(),
            date: t.transactDate,
            type: formatAccount(t),
            inAmount: t.inAccountId === accountId ? t.amount : 0,
            outAmount: t.outAccountId === accountId ? t.amount : 0,
            detail: t.detail,
            locked:
                (!!account.inLockDate && t.inAccountId === account.id && t.transactDate <= account.inLockDate) ||
                (!!account.outLockDate && t.outAccountId === account.id && t.transactDate <= account.outLockDate),
            onClick: () => dialog.openDialog('editTrans', {transId: t.id}),
        }));
    }, [transactions]);

    const handleToday = () => {
        setSearchParams({
            ymdFrom: new FtDate().setDate(1).ymd('-'),
            ymdTo: new FtDate().lastDayOfMonth().ymd('-'),
        });
    }

    const handleMove = (delta: number) => {
        setSearchParams({
            ymdFrom: new FtDate(ymdFrom).addMonth(delta).ymd('-'),
            ymdTo: new FtDate(ymdTo).addMonth(delta).lastDayOfMonth().ymd('-'),
        });
    }

    function formatAccount(transaction: TransDto) {
        if (transaction.outAccountId === accountId) {
            return `→ ${getAccountNameById(transaction.inAccountId)}`;
        } else if (transaction.inAccountId === accountId) {
            return `${getAccountNameById(transaction.outAccountId)} →`;
        } else {
            return `${getAccountNameById(transaction.outAccountId)} → ${getAccountNameById(transaction.inAccountId)}`;
        }
    }

    if (!account) return <ErrorFull error="계정을 찾을 수 없습니다."/>;
    if (error) return <ErrorFull error={ErrorUtil.getErrorMessage(error)}/>;
    if (!sumBefore) return <LoadingFull/>; // TODO

    let balance: number | undefined = undefined;
    if (account.type !== 'INC' && account.type !== 'EXP') {
        balance = account.startAmount + sumBefore.inSum - sumBefore.outSum;
    }

    return (
        <div>
            <h2>{account?.name}</h2>

            <div style={{display: "flex", alignItems: "center", flexWrap: "wrap", gap: 16, marginBottom: 16}}>

                <form onSubmit={() => setSearchParams({...searchParamsInput})}>
                    <div style={{display: 'flex', gap: 8}}>
                        <DateInput value={searchParamsInput.ymdFrom}
                                   required={true}
                                   onChange={(d) => setSearchParamsInput({
                                       ...searchParamsInput, ymdFrom: d
                                   })}
                        />
                        <DateInput value={searchParamsInput.ymdTo}
                                   required={true}
                                   onChange={(d) => setSearchParamsInput({
                                       ...searchParamsInput, ymdTo: d
                                   })}
                        />
                        <FtButton style={{minWidth: 80}} type="submit">조회</FtButton>
                    </div>
                </form>

                <DateNav onToday={handleToday} onPrev={() => handleMove(-1)} onNext={() => handleMove(1)}/>

                {!Device.isMobile && account.type === "CARD" && (<>
                    <div style={{flexGrow: 1}}/>
                    <div>
                        <Link to={`/Account/${accountId}/closeCard`}>맞추기</Link>
                    </div>
                </>)}
            </div>

            <TransactionListView prevBalance={balance} items={items} splitInOut={account.type !== 'INC' && account.type !== 'EXP'} reverseInOut={account.type === 'INC' || account.type === 'EXP'}/>

            {(items?.length ?? 0) === MAX_TRANS_LIST_ITEM_COUNT && (
                <div style={{marginTop: '1rem', color: '#999', fontSize: 12, textAlign: 'center'}}>
                    검색 결과가 {MAX_TRANS_LIST_ITEM_COUNT}건으로 제한됩니다. 조건을 추가하여 검색해 주세요.
                </div>
            )}

            {!transactions && <LoadingFull/>}
        </div>
    );
}
